
import { useEffect, useMemo, useState } from 'react';
import Link from '@/components/compat/Link';
import {
  fetchAllpowersRequired,
  type AllpowersRequiredPower,
} from '@/lib/allpowers-data';
import { getAllpowersCatalogFallback } from '@/lib/allpowers-catalog-fallback';
import {
  AceDarkPanelShell,
  aceDarkChipActiveClass,
  aceDarkChipClass,
  aceDarkChipInactiveClass,
  aceDarkControlClass,
  aceDarkDividerClass,
  aceDarkInputClass,
} from '@/components/help-center/aces/AceDarkPanelShell';
import { AceSortDropdown } from '@/components/help-center/aces/AceSortDropdown';
import { getPowerCategoryLabel, POWERS_PAGE_SIZE } from '@/lib/powers-data';
import { GsImage, ORBITAL_ICON_BOX_STYLE, cn } from '@/components/help-center/latest-power-shared';
import { AlertCircle, ArrowRight, ChevronLeft, ChevronRight, Infinity, Lock, Search } from 'lucide-react';

type FilterId = 'all' | 'unlimited' | 'limited';
type SortId = 'id-desc' | 'name-asc';

const FILTER_OPTIONS: { id: FilterId; label: string }[] = [
  { id: 'all', label: 'Todos' },
  { id: 'unlimited', label: 'Ilimitados' },
  { id: 'limited', label: 'Limitados' },
];

const SORT_OPTIONS: { id: SortId; label: string }[] = [
  { id: 'id-desc', label: 'ID (recentes)' },
  { id: 'name-asc', label: 'Nome A–Z' },
];

function getPageNumbers(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | 'ellipsis')[] = [1];
  if (current > 3) pages.push('ellipsis');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);

  if (current < total - 2) pages.push('ellipsis');
  pages.push(total);
  return pages;
}

const powerRowClass = cn(
  'group flex items-center gap-3 rounded-xl px-3 py-2.5',
  'border border-white/[0.08] bg-gradient-to-br from-white/[0.07] to-white/[0.02]',
  'shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-sm',
  'transition-all duration-200 hover:border-sky-400/30 hover:from-white/[0.09] hover:-translate-y-0.5'
);

const darkFieldClass = cn(
  aceDarkInputClass,
  'font-inter text-sm text-blue-50 placeholder:text-blue-200/35'
);

function RequiredPowerRow({ power }: { power: AllpowersRequiredPower }) {
  return (
    <Link href={`/powers/${power.slug}`} className={powerRowClass}>
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl"
        style={ORBITAL_ICON_BOX_STYLE}
      >
        <GsImage
          code={power.slug}
          className="flex h-full w-full items-center justify-center"
          imgClassName="max-h-8 max-w-8 object-contain"
        />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p
            className="truncate font-poppins text-sm font-semibold text-white"
            style={{
              textShadow: '0 0 16px rgba(255,255,255,0.12), 0 0 4px rgba(255,255,255,0.06)',
            }}
          >
            {power.name}
          </p>
          {power.limited ? (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-poppins text-[10px] font-bold uppercase tracking-wide text-white"
              style={{
                background: 'linear-gradient(90deg, #e8620a 0%, #f47c1a 50%, #e8a020 100%)',
                boxShadow: '0 0 0 1px rgba(240,130,30,0.35)',
              }}
            >
              <Lock className="h-2.5 w-2.5" aria-hidden />
              Limitado
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-cyan-500/30 to-sky-500/30 px-2 py-0.5 font-poppins text-[10px] font-bold uppercase tracking-wide text-cyan-300 ring-1 ring-cyan-400/35">
              <Infinity className="h-2.5 w-2.5" aria-hidden />
              Ilimitado
            </span>
          )}
        </div>
        <p className="mt-0.5 font-inter text-xs text-blue-100/60">
          ID {power.id}
          {power.category ? ` · ${getPowerCategoryLabel(power.category)}` : null}
        </p>
      </div>

      <ArrowRight
        className="h-4 w-4 shrink-0 text-sky-300/30 transition-all group-hover:translate-x-0.5 group-hover:text-sky-300"
        aria-hidden
      />
    </Link>
  );
}

function ListSkeleton() {
  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 9 }).map((_, index) => (
        <div
          key={index}
          className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.04] px-3 py-2.5"
        >
          <div className="h-11 w-11 animate-pulse rounded-xl bg-white/[0.08]" />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-2/3 animate-pulse rounded bg-white/[0.08]" />
            <div className="h-3 w-1/2 animate-pulse rounded bg-white/[0.06]" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function AllpowersRequiredList() {
  const [powers, setPowers] = useState<AllpowersRequiredPower[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterId>('all');
  const [sort, setSort] = useState<SortId>('id-desc');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await fetchAllpowersRequired();
        if (cancelled) return;
        setPowers(data.powers);
        setSource(data.source);
        setError(null);
      } catch {
        if (cancelled) return;
        const fallback = getAllpowersCatalogFallback();
        if (fallback.length > 0) {
          setPowers(fallback);
          setSource('catalog-fallback');
          setError('Lista actualizada parcialmente a partir do catálogo local.');
        } else {
          setError('Não foi possível carregar a lista de powers necessários.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    let result = [...powers];

    if (filter === 'limited') result = result.filter((power) => power.limited);
    if (filter === 'unlimited') result = result.filter((power) => !power.limited);

    if (query) {
      result = result.filter(
        (power) =>
          power.name.toLowerCase().includes(query) ||
          power.slug.includes(query) ||
          String(power.id).includes(query)
      );
    }

    if (sort === 'name-asc') {
      result.sort((a, b) => a.name.localeCompare(b.name, 'pt'));
    } else {
      result.sort((a, b) => b.id - a.id);
    }

    return result;
  }, [powers, search, filter, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / POWERS_PAGE_SIZE));

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * POWERS_PAGE_SIZE;
    return filtered.slice(start, start + POWERS_PAGE_SIZE);
  }, [filtered, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filter, sort]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pageNumbers = getPageNumbers(currentPage, totalPages);
  const rangeStart = filtered.length === 0 ? 0 : (currentPage - 1) * POWERS_PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * POWERS_PAGE_SIZE, filtered.length);

  const limitedCount = useMemo(() => powers.filter((power) => power.limited).length, [powers]);
  const unlimitedCount = powers.length - limitedCount;

  return (
    <AceDarkPanelShell
      className="shadow-xl shadow-black/35"
      innerClassName="px-5 py-5 md:px-6 md:py-6"
    >
      <div
        className={cn(
          'mb-6 flex flex-col gap-4 border-b pb-6 lg:flex-row lg:items-end lg:justify-between',
          aceDarkDividerClass
        )}
      >
        <div className="flex items-start gap-4">
          <div
            className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl md:h-[4.5rem] md:w-[4.5rem]"
            style={ORBITAL_ICON_BOX_STYLE}
          >
            <GsImage
              code="allpowers"
              className="flex h-full w-full items-center justify-center"
              imgClassName="max-h-12 max-w-12 object-contain md:max-h-14 md:max-w-14"
            />
          </div>

          <div className="min-w-0 pt-0.5">
            <h3 className="font-poppins text-lg font-bold text-white md:text-xl">Powers necessários</h3>
            <p className="mt-1 max-w-2xl font-inter text-sm leading-relaxed text-blue-100/70">
              Lista actualizada via FairTrade xatblog. Clique num power para abrir a página interna
              com smilies, peões e detalhes.
            </p>
          </div>
        </div>

        {!loading && powers.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full border border-white/[0.12] bg-white/[0.08] px-3 py-1 font-poppins text-xs font-semibold text-blue-50">
              {powers.length} no total
            </span>
            <span className="rounded-full bg-gradient-to-r from-cyan-500/25 to-sky-500/25 px-3 py-1 font-poppins text-xs font-semibold text-cyan-200 ring-1 ring-cyan-400/30">
              {unlimitedCount} ilimitados
            </span>
            <span
              className="rounded-full px-3 py-1 font-poppins text-xs font-semibold text-white"
              style={{
                background: 'linear-gradient(90deg, #e8620a 0%, #f47c1a 50%, #e8a020 100%)',
                boxShadow: '0 0 0 1px rgba(240,130,30,0.35)',
              }}
            >
              {limitedCount} limitados
            </span>
          </div>
        ) : null}
      </div>

      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center">
        <label className="relative flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-amber-200/70"
            aria-hidden
          />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por nome, slug ou ID..."
            className={cn(darkFieldClass, 'w-full py-2.5 pl-10 pr-3')}
          />
        </label>

        <div className="flex flex-wrap items-center gap-2">
          {FILTER_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setFilter(option.id)}
              className={cn(
                aceDarkChipClass,
                filter === option.id ? aceDarkChipActiveClass : aceDarkChipInactiveClass
              )}
            >
              {option.label}
            </button>
          ))}

          <div className="ml-0 flex flex-wrap items-center gap-2 sm:ml-2">
            <span className="font-poppins text-xs font-semibold uppercase tracking-wide text-amber-200/75">
              Ordenar
            </span>
            <AceSortDropdown
              value={sort}
              options={SORT_OPTIONS}
              onChange={setSort}
              ariaLabel="Ordenar powers AllPowers"
            />
          </div>
        </div>
      </div>

      {loading ? <ListSkeleton /> : null}

      {!loading && error ? (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-400/25 bg-amber-500/10 px-3 py-2.5">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" aria-hidden />
          <p className="font-inter text-sm text-amber-100">{error}</p>
        </div>
      ) : null}

      {!loading && !error && filtered.length === 0 ? (
        <p className="py-8 text-center font-inter text-sm text-blue-100/50">
          Nenhum power encontrado com estes filtros.
        </p>
      ) : null}

      {!loading && filtered.length > 0 ? (
        <>
          <p className="mb-3 font-inter text-xs text-blue-100/50">
            A mostrar {rangeStart}–{rangeEnd} de {filtered.length} powers
            {filtered.length !== powers.length ? ` (filtrados de ${powers.length})` : null}
            {source === 'catalog-fallback' ? ' · catálogo local' : null}
          </p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {paginated.map((power) => (
              <RequiredPowerRow key={`${power.slug}-${power.id}`} power={power} />
            ))}
          </div>

          {totalPages > 1 ? (
            <nav
              className="mt-8 flex flex-wrap items-center justify-center gap-2"
              aria-label="Paginação da lista AllPowers"
            >
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={currentPage === 1}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 font-poppins text-sm font-medium transition-colors',
                  aceDarkControlClass,
                  currentPage === 1
                    ? 'cursor-not-allowed opacity-40'
                    : 'text-blue-100/80 hover:text-blue-50'
                )}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
                Anterior
              </button>

              <div className="flex items-center gap-1">
                {pageNumbers.map((page, index) =>
                  page === 'ellipsis' ? (
                    <span key={`ellipsis-${index}`} className="px-2 font-poppins text-blue-100/40">
                      …
                    </span>
                  ) : (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setCurrentPage(page)}
                      className={cn(
                        'min-w-[40px] rounded-xl px-3 py-2 font-poppins text-sm font-medium transition-colors',
                        currentPage === page
                          ? cn(aceDarkChipClass, aceDarkChipActiveClass)
                          : cn(aceDarkControlClass, 'text-blue-100/80 hover:text-blue-50')
                      )}
                      aria-current={currentPage === page ? 'page' : undefined}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>

              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                disabled={currentPage === totalPages}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 font-poppins text-sm font-medium transition-colors',
                  aceDarkControlClass,
                  currentPage === totalPages
                    ? 'cursor-not-allowed opacity-40'
                    : 'text-blue-100/80 hover:text-blue-50'
                )}
              >
                Próxima
                <ChevronRight className="h-4 w-4" aria-hidden />
              </button>
            </nav>
          ) : null}
        </>
      ) : null}
    </AceDarkPanelShell>
  );
}
